import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '../api'
import { useToast } from '../components/Toast'
import { useAsyncData } from '../hooks/useAsyncData'
import Spinner, { PageSpinner } from '../components/Spinner'
import ConfirmDialog from '../components/ConfirmDialog'
import {
  ACCOUNT_TYPE_COLORS, ACCOUNT_TYPE_LABELS, ENV_COLORS,
} from '../constants'
import Instant from '../components/Instant'

function Field({ label, children }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide opacity-40">{label}</dt>
      <dd className="mt-0.5">{children ?? '—'}</dd>
    </div>
  )
}

export default function AccountDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [password, setPassword] = useState(null)
  const [loadingPassword, setLoadingPassword] = useState(false)
  const [copied, setCopied] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [confirmRemove, setConfirmRemove] = useState(false)
  const { show } = useToast()

  const { data, loading, error, reload } = useAsyncData(async () => {
    const [account, reservations] = await Promise.all([
      api.getAccount(id),
      api.getReservations({ includeAll: true }),
    ])
    return { account, reservations }
  }, [id], { onError: err => show(err.message, 'error') })

  const account = data?.account
  const reservation = data?.reservations?.find(
    r => r.reservable_type === 'ACCOUNT' && r.reservable_id === id
  )

  // The password lives behind its own endpoint so reads stay explicit rather than
  // riding along with every account fetch — so it's fetched on demand, not on load.
  async function revealPassword() {
    setLoadingPassword(true)
    try {
      const creds = await api.getAccountCredentials(id)
      setPassword(creds.password ?? '')
    } catch (err) {
      show(err.message, 'error')
    } finally {
      setLoadingPassword(false)
    }
  }

  async function copyPassword() {
    try {
      await navigator.clipboard.writeText(password)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      show('Could not copy to clipboard', 'error')
    }
  }

  async function removeAccount() {
    setRemoving(true)
    try {
      await api.deleteAccount(id)
      show(`Removed "${account.email}"`, 'success')
      navigate('/accounts')
    } catch (err) {
      show(err.message, 'error')
      setConfirmRemove(false)
    } finally {
      setRemoving(false)
    }
  }

  if (loading) return <PageSpinner />

  if (error) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <Link to="/accounts" className="link link-hover text-sm">← Accounts</Link>
        <div className="text-center py-20">
          <p className="opacity-60 mb-4">Failed to load account: {error.message}</p>
          <button className="btn btn-sm btn-primary" onClick={reload}>Retry</button>
        </div>
      </div>
    )
  }

  if (!account) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <Link to="/accounts" className="link link-hover text-sm">← Accounts</Link>
        <div className="text-center py-20 opacity-40">Account not found</div>
      </div>
    )
  }

  const isPartner = account.account_type === 'PARTNER'
  const feds = [
    account.federation_emporia && 'emporia',
    account.federation_google && 'google',
    account.federation_apple && 'apple',
  ].filter(Boolean)

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      <Link to="/accounts" className="link link-hover text-sm opacity-60">← Accounts</Link>

      <div className="flex items-center gap-3 mt-2 mb-6 flex-wrap">
        <h1 className="text-2xl font-bold break-all">{account.email}</h1>
        <span className={`badge ${ACCOUNT_TYPE_COLORS[account.account_type] ?? 'badge-ghost'}`}>
          {ACCOUNT_TYPE_LABELS[account.account_type] ?? account.account_type}
        </span>
        <span className={`badge ${ENV_COLORS[account.environment] ?? 'badge-neutral'}`}>
          {account.environment}
        </span>
        <button
          className="btn btn-sm btn-ghost text-error ml-auto"
          disabled={removing || !!reservation}
          aria-busy={removing}
          title={reservation ? 'Reserved accounts cannot be removed' : undefined}
          onClick={() => setConfirmRemove(true)}
        >
          {removing ? <Spinner size="xs" /> : 'Remove account'}
        </button>
      </div>

      <ConfirmDialog
        open={confirmRemove}
        title="Remove account"
        message={`Remove account "${account.email}" from inventory? This cannot be undone.`}
        confirmLabel="Remove"
        busy={removing}
        onConfirm={removeAccount}
        onClose={() => setConfirmRemove(false)}
      />

      {/* Credentials — the reason this page exists */}
      <div className="rounded-box border border-base-300 p-5 mb-6">
        <h2 className="font-semibold mb-3">Credentials</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <Field label="Email">
            <span className="font-mono break-all">{account.email}</span>
          </Field>
          <Field label="Password">
            {password === null ? (
              <button
                className="btn btn-xs btn-outline"
                onClick={revealPassword}
                disabled={loadingPassword}
                aria-busy={loadingPassword}
              >
                {loadingPassword ? <Spinner size="xs" /> : 'Show password'}
              </button>
            ) : password === '' ? (
              <span className="opacity-40">not set</span>
            ) : (
              <span className="flex items-center gap-2 flex-wrap">
                <code className="font-mono bg-base-300/60 rounded px-1.5 py-0.5 break-all">
                  {password}
                </code>
                <button className="btn btn-xs btn-ghost" onClick={copyPassword}>
                  {copied ? 'copied' : 'copy'}
                </button>
              </span>
            )}
          </Field>
        </dl>
        <p className="text-xs opacity-40 mt-3">
          QA-only credential. Never returned by account listings — it is read from a
          separate endpoint so the access is deliberate.
        </p>
      </div>

      {/* Account info */}
      <div className="rounded-box border border-base-300 p-5 mb-6">
        <h2 className="font-semibold mb-3">Account info</h2>
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
          <Field label="Type">
            {ACCOUNT_TYPE_LABELS[account.account_type] ?? account.account_type}
          </Field>
          <Field label="Environment">{account.environment}</Field>
          <Field label="Created">
            <Instant iso={account.created_at} />
          </Field>
          {isPartner ? (
            <>
              <Field label="Company">{account.partner_company}</Field>
              <Field label="Role">{account.partner_role}</Field>
              <Field label="Partner GID">{account.partner_gid}</Field>
              <Field label="Public charger">
                {account.has_public_charger ? (
                  <span className="badge badge-sm badge-warning">yes</span>
                ) : (
                  <span className="opacity-40">no</span>
                )}
              </Field>
            </>
          ) : (
            <Field label="Federations">
              {feds.length > 0 ? (
                <span className="flex gap-1 flex-wrap">
                  {feds.map(f => (
                    <span key={f} className="badge badge-sm badge-outline">{f}</span>
                  ))}
                </span>
              ) : (
                <span className="opacity-40">none</span>
              )}
            </Field>
          )}
          <Field label="Last synced">
            <Instant iso={account.synced_at} />
          </Field>
        </dl>
        {isPartner && (
          <p className="text-xs opacity-40 mt-3">
            Partner accounts sign in through the partner portal, which is a separate
            Cognito pool — so federations do not apply.
            {account.has_public_charger &&
              ' This login has a public charger on it; the charger is not tracked as a' +
              ' device, so reserving this account is how you get it.'}
          </p>
        )}
      </div>

      {/* Reservation */}
      <div className="rounded-box border border-base-300 p-5">
        <h2 className="font-semibold mb-3">Reservation</h2>
        {reservation ? (
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            <Field label="Status">
              <span className="badge badge-sm badge-error">Reserved</span>
            </Field>
            <Field label="Reserved by">
              {reservation.reserved_by_email ?? reservation.reserved_by}
            </Field>
            <Field label="Since">
              <Instant iso={reservation.created_at} />
            </Field>
            {reservation.bundle_id && (
              <Field label="Bundle">
                <Link to="/bundles" className="link link-hover font-mono text-xs">
                  {reservation.bundle_id.slice(0, 8)}…
                </Link>
              </Field>
            )}
          </dl>
        ) : (
          <p className="text-sm opacity-40">Not reserved.</p>
        )}
      </div>
    </div>
  )
}
